import React, { useState, useMemo } from 'react';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Pagination from '../../common/Pagination';
import Toast, { ToastMessage } from '../../common/Toast';
import NoDataFound from '../../common/NoDataFound';
import { confirmDelete, confirmWarning, confirmActive } from '../../common/confirm';
import { Plus, Save, Check, CircleMinus, CircleCheck, Search } from 'lucide-react';
import Badge from '../../common/Badge';
import Skeleton from '../../common/Skeleton';
import { statusVariant, activeBadgeCss } from '../../../utils/statusVariant';

// --- Types ---

interface RiskRegister {
    id: number;
    risk_code: string;
    risk_name: string;
    risk_description: string;
    risk_category_id: number;
    category_name?: string;
    start_date: string;
    end_date?: string;
    status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';
    is_active: boolean;
    display_order: number;
    rejection_reason?: string;
}

interface RiskCategory {
    id: number;
    category_name: string;
    category_code: string;
}

// --- Mock Data ---

const MOCK_CATEGORIES: RiskCategory[] = [
    { id: 1, category_name: 'Strategic Risk', category_code: 'STR-001' },
    { id: 2, category_name: 'Operational Risk', category_code: 'OPS-001' },
    { id: 3, category_name: 'Financial Risk', category_code: 'FIN-001' },
    { id: 4, category_name: 'Compliance Risk', category_code: 'CMP-001' },
    { id: 5, category_name: 'Cybersecurity Risk', category_code: 'CYB-001' },
];

const MOCK_RISKS: RiskRegister[] = [
    { id: 1, risk_code: 'RISK-001', risk_name: 'Data Breach', risk_description: 'Unauthorized access to sensitive data.', risk_category_id: 5, category_name: 'Cybersecurity Risk', start_date: '2023-01-01', status: 'APPROVED', is_active: true, display_order: 10 },
    { id: 2, risk_code: 'RISK-002', risk_name: 'Supply Chain Disruption', risk_description: 'Failure of key suppliers.', risk_category_id: 2, category_name: 'Operational Risk', start_date: '2023-02-15', status: 'APPROVED', is_active: true, display_order: 20 },
    { id: 3, risk_code: 'RISK-003', risk_name: 'Regulatory Fine', risk_description: 'Non-compliance with GDPR.', risk_category_id: 4, category_name: 'Compliance Risk', start_date: '2023-03-10', status: 'PENDING_APPROVAL', is_active: true, display_order: 30 },
    { id: 4, risk_code: 'RISK-004', risk_name: 'Market Volatility', risk_description: 'Sudden drop in stock prices.', risk_category_id: 3, category_name: 'Financial Risk', start_date: '2023-04-05', status: 'DRAFT', is_active: true, display_order: 40 },
    { id: 5, risk_code: 'RISK-005', risk_name: 'Reputation Loss', risk_description: 'Negative social media campaign.', risk_category_id: 1, category_name: 'Strategic Risk', start_date: '2023-05-20', status: 'REJECTED', is_active: false, display_order: 50, rejection_reason: 'Needs more detail.' },
];

const STATUS_LABELS: Record<string, string> = {
    APPROVED: 'Approved',
    DRAFT: 'Draft',
    PENDING_APPROVAL: 'Pending',
    REJECTED: 'Rejected',
};

const StatusBadge = ({ status }: { status: string }) => (
    <Badge variant={statusVariant(status)} size="sm">
        {STATUS_LABELS[status] || status}
    </Badge>
);

// --- Form modal ---

interface FormData {
    risk_code: string;
    risk_name: string;
    risk_description: string;
    risk_category_id: number | '';
    start_date: string;
}

const EMPTY_FORM: FormData = {
    risk_code: '',
    risk_name: '',
    risk_description: '',
    risk_category_id: '',
    start_date: new Date().toISOString().split('T')[0],
};

const RiskFormModal = ({
    isOpen, onClose, initial, onSubmit, onDelete, onToggleActive, toggling,
}: {
    isOpen: boolean;
    onClose: () => void;
    initial?: RiskRegister | null;
    onSubmit: (data: FormData) => Promise<void>;
    onDelete?: () => Promise<void>;
    onToggleActive?: () => Promise<void>;
    toggling?: boolean;
}) => {
    const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);

    React.useEffect(() => {
        setFormData(initial ? {
            risk_code: initial.risk_code,
            risk_name: initial.risk_name,
            risk_description: initial.risk_description,
            risk_category_id: initial.risk_category_id,
            start_date: initial.start_date,
        } : EMPTY_FORM);
        setSubmitting(false);
        setDeleting(false);
    }, [initial, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try { await onSubmit(formData); } finally { setSubmitting(false); }
    };

    const handleDelete = async () => {
        if (!onDelete) return;
        setDeleting(true);
        try { await onDelete(); } finally { setDeleting(false); }
    };

    const categoryOptions = MOCK_CATEGORIES.map(c => ({ value: c.id, label: c.category_name }));
    const busy = submitting || deleting || !!toggling;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>
                {initial
                    ? <>Editing "<span className="item-highlight">{initial.risk_name}</span>"</>
                    : 'Create Risk'}
            </Modal.Header>
            <Modal.Body>
                <form id="risk-mock-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
                    <FormControl.Input
                        label="Risk Code"
                        required
                        value={formData.risk_code}
                        onChange={(e) => setFormData({ ...formData, risk_code: e.target.value.toUpperCase() })}
                        placeholder="e.g. RISK-001"
                    />
                    <FormControl.Input
                        label="Risk Name"
                        required
                        value={formData.risk_name}
                        onChange={(e) => setFormData({ ...formData, risk_name: e.target.value })}
                    />
                    <FormControl.Textarea
                        label="Description"
                        value={formData.risk_description}
                        onChange={(e) => setFormData({ ...formData, risk_description: e.target.value })}
                        rows={3}
                    />
                    <FormControl.SearchSelect
                        label="Category"
                        options={categoryOptions}
                        value={formData.risk_category_id !== '' ? Number(formData.risk_category_id) : null}
                        onChange={(v) => setFormData({ ...formData, risk_category_id: v !== null ? Number(v) : '' })}
                        placeholder="— Select category —"
                        clearable
                    />
                    <FormControl.Input
                        label="Start Date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className={initial ? 'app-modal-footer--split' : ''}>
                {initial && onToggleActive && (
                    <Button
                        variant={initial.is_active ? 'inactive-outline' : 'active-outline'}
                        icon={initial.is_active ? CircleMinus : CircleCheck}
                        onClick={onToggleActive}
                        disabled={busy}
                    >
                        {toggling ? '...' : initial.is_active ? 'Inactive' : 'Active'}
                    </Button>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                    {initial && onDelete && (
                        <Button variant="delete-outline" onClick={handleDelete} disabled={busy}>
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        form="risk-mock-form"
                        variant={initial ? 'update' : 'fill'}
                        icon={initial ? Check : Save}
                        disabled={busy}
                    >
                        {submitting
                            ? (initial ? 'Updating...' : 'Submitting...')
                            : (initial ? 'Update' : 'Submit')}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

// --- Rejection modal ---

const RejectionModal = ({
    isOpen, onClose, onConfirm,
}: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
}) => {
    const [reason, setReason] = useState('');

    React.useEffect(() => { setReason(''); }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm">
            <Modal.Header>Reject Risk</Modal.Header>
            <Modal.Body>
                <FormControl.Textarea
                    label="Rejection Reason"
                    required
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="fill" onClick={() => { if (reason) onConfirm(reason); }} disabled={!reason}>
                    Confirm Rejection
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// --- Main component ---

const RiskRegisterMock: React.FC = () => {
    const [risks, setRisks] = useState<RiskRegister[]>(MOCK_RISKS);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [showModal, setShowModal] = useState(false);
    const [selectedRisk, setSelectedRisk] = useState<RiskRegister | null>(null);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const loading = false;

    const filteredRisks = useMemo(() => risks.filter(r => {
        const matchesSearch = r.risk_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.risk_code.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'ALL' || r.status === statusFilter;
        return matchesSearch && matchesStatus;
    }), [risks, searchTerm, statusFilter]);

    const paginatedRisks = filteredRisks.slice((page - 1) * pageSize, page * pageSize);

    const handleCreate = () => {
        setSelectedRisk(null);
        setShowModal(true);
    };

    const handleRowClick = (risk: RiskRegister) => {
        setSelectedRisk(risk);
        setShowModal(true);
    };

    const handleSubmit = async (data: FormData) => {
        const category = MOCK_CATEGORIES.find(c => c.id === Number(data.risk_category_id));
        if (selectedRisk) {
            setRisks(prev => prev.map(r => r.id === selectedRisk.id
                ? { ...r, ...data, risk_category_id: Number(data.risk_category_id), category_name: category?.category_name }
                : r));
            setToast({ type: 'success', message: 'Risk updated.' });
        } else {
            const newRisk: RiskRegister = {
                ...data,
                id: Date.now(),
                risk_category_id: Number(data.risk_category_id),
                category_name: category?.category_name,
                status: 'DRAFT',
                is_active: true,
                display_order: 999,
            };
            setRisks(prev => [...prev, newRisk]);
            setToast({ type: 'success', message: 'Risk created.' });
        }
        setShowModal(false);
    };

    const handleDelete = async () => {
        if (!selectedRisk) return;
        const ok = await confirmDelete({ text: `This will permanently delete "${selectedRisk.risk_name}".` });
        if (!ok) return;
        setRisks(prev => prev.filter(r => r.id !== selectedRisk.id));
        setShowModal(false);
        setToast({ type: 'delete', message: 'Risk deleted.' });
    };

    const handleToggleActive = async () => {
        if (!selectedRisk) return;
        const isActive = selectedRisk.is_active;
        const next = !isActive;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this risk?' : 'Inactivate this risk?',
            text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            const updated = { ...selectedRisk, is_active: next };
            setRisks(prev => prev.map(r => r.id === selectedRisk.id ? updated : r));
            setSelectedRisk(updated);
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated.' : 'Inactivated.' });
        } finally {
            setToggling(false);
        }
    };

    const handleRejectConfirm = (reason: string) => {
        if (!selectedRisk) return;
        const updated = { ...selectedRisk, status: 'REJECTED' as const, rejection_reason: reason };
        setRisks(prev => prev.map(r => r.id === selectedRisk.id ? updated : r));
        setShowRejectionModal(false);
        setShowModal(false);
        setToast({ type: 'success', message: 'Risk rejected.' });
    };

    return (
        <div className="netbody-card flex flex-col h-full">

            {/* Header */}
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.SearchSelect
                        value={statusFilter}
                        onChange={v => { setStatusFilter(String(v ?? 'ALL')); setPage(1); }}
                        className="filter-status"
                        noSearchIcon
                        options={[
                            { value: 'ALL', label: 'All Status' },
                            { value: 'DRAFT', label: 'Draft' },
                            { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                            { value: 'APPROVED', label: 'Approved' },
                            { value: 'REJECTED', label: 'Rejected' },
                        ]}
                    />
                    <FormControl.Input
                        placeholder="Search risks…"
                        value={searchTerm}
                        onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                    />
                    <Button variant="fill" icon={Plus} onClick={handleCreate}>Create</Button>
                    {/* <span style={{ padding: '4px 8px', background: '#fef3c7', color: '#d97706', borderRadius: '4px', fontSize: '12px', fontWeight: 600, border: '1px solid #fcd34d', whiteSpace: 'nowrap' }}>
                        Mock Mode
                    </span> */}
                </div>
            </div>

            {/* Content */}
            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Risk Name</th>
                                        <th className="table-list-th">Category</th>
                                        <th className="table-list-th">Status</th>
                                        <th className="table-list-th">Active</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {!loading && filteredRisks.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="table-list-cell">
                                                <NoDataFound
                                                    title={searchTerm || statusFilter !== 'ALL' ? 'No Matching Risks' : 'No Risks Found'}
                                                    message={searchTerm || statusFilter !== 'ALL' ? 'No risks match your search or filter.' : 'Click "Create" to add one.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {loading && <Skeleton.TableRows cols={['3xs', 'xs', 'md', 'sm', 'pill', { width: 'icon', align: 'right' }]} />}
                                    {!loading && paginatedRisks.map((risk, idx) => (
                                        <tr
                                            key={risk.id}
                                            className="table-list-row"
                                            onClick={() => handleRowClick(risk)}
                                        >
                                            <td className="table-list-cell table-list-cell--sno">
                                                {(page - 1) * pageSize + idx + 1}
                                            </td>
                                            <td className="table-list-cell">{risk.risk_code}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">{risk.risk_name}</span>
                                                {risk.risk_description && (
                                                    <span className="table-list-cell-sub">{risk.risk_description}</span>
                                                )}
                                            </td>
                                            <td className="table-list-cell">{risk.category_name || '—'}</td>
                                            <td className="table-list-cell">
                                                <StatusBadge status={risk.status} />
                                            </td>
                                            <td className="table-list-cell">
                                                <span className={activeBadgeCss(risk.is_active)}>
                                                    {risk.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={filteredRisks.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="risks"
                        />
                    </div>
                </div>
            </div>

            {/* Form modal */}
            <RiskFormModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                initial={selectedRisk}
                onSubmit={handleSubmit}
                onDelete={selectedRisk ? handleDelete : undefined}
                onToggleActive={selectedRisk ? handleToggleActive : undefined}
                toggling={toggling}
            />

            {/* Rejection modal */}
            <RejectionModal
                isOpen={showRejectionModal}
                onClose={() => setShowRejectionModal(false)}
                onConfirm={handleRejectConfirm}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default RiskRegisterMock;
